// 캘린더 메모 CRUD + 실시간 구독.
// docs/13-calendar-design.md §4.2 + 사무소 공유 정책 (2026-05-25).
//
// 공유 모델:
//   - appraisalId 없음 → 작성자 본인만
//   - appraisalId 있음 → 사무소 전체 (level ≥ 2)
//
// ⚠️ Firestore rules-are-not-filters:
//    rule 의 OR 가지마다 query 가 같이 매칭해야 함.
//      본인 메모 ↔ where('ownerId', '==', uid)
//      공유 메모 ↔ where('appraisalId', '!=', null)
//      특정 사건 ↔ where('appraisalId', '==', id)
//    이 외 형태는 권한 거부.

import {
  Timestamp,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
  type FieldValue
} from 'firebase/firestore';

import { db } from 'firebase/config';

export type NoteCategory = 'meeting' | 'visit' | 'work_block' | 'followup' | 'personal' | 'other';

export const NOTE_CATEGORY_LABELS: Record<NoteCategory, string> = {
  meeting: '회의',
  visit: '방문',
  work_block: '작업',
  followup: 'follow-up',
  personal: '개인',
  other: '기타'
};

export const NOTE_CATEGORIES: NoteCategory[] = ['meeting', 'visit', 'work_block', 'followup', 'personal', 'other'];

export interface CalendarNote {
  id: string;
  ownerId: string;
  title: string;
  description?: string;
  startAt: Timestamp;
  endAt?: Timestamp;
  allDay: boolean;
  appraisalId?: string; // 사건 연결 (선택)
  // 사건 연결 시 사무소 공유 여부. 사건 연결 안 됐으면 항상 개인 메모(이 필드 무의미).
  // 누락 = true (옛 데이터 호환 — rule 의 .get('isShared', true) 와 lazy migration 이 처리)
  isShared?: boolean;
  category: NoteCategory;
  reminderMinutesBefore?: number; // 0/30/60/1440 등. undefined = 알림 없음
  createdAt?: Timestamp | FieldValue;
  updatedAt?: Timestamp | FieldValue;
}

// 새 메모 저장 시 호출자가 채워서 보내는 형태 (id 는 호출자가 생성)
export type CalendarNoteInput = Omit<CalendarNote, 'createdAt' | 'updatedAt'>;

const COLLECTION = 'calendar_notes';

/**
 * 본인 메모 + 사무소 공유 메모 (다른 사람이 사건에 연결한 메모) 합쳐서 실시간 구독.
 * 두 개 onSnapshot 을 합쳐 dedup 후 콜백 호출. (Firestore 는 OR 쿼리가 제한적이라 분리)
 */
export function subscribeToCalendarNotes(
  uid: string,
  onChange: (notes: CalendarNote[]) => void,
  onError?: (e: Error) => void
): () => void {
  let owned: CalendarNote[] = [];
  let shared: CalendarNote[] = [];
  let ownedReady = false;
  let sharedReady = false;

  const emit = () => {
    if (!ownedReady || !sharedReady) return; // 첫 emit 은 양쪽 다 도착 후 (깜빡임 방지)
    const map = new Map<string, CalendarNote>();
    for (const n of owned) map.set(n.id, n);
    for (const n of shared) if (!map.has(n.id)) map.set(n.id, n); // 본인이 만들고 사건 단 거 중복 제거
    onChange(Array.from(map.values()));
  };

  const handleError = (e: Error) => {
    if (onError) onError(e);
  };

  const unsubOwned = onSnapshot(
    query(collection(db, COLLECTION), where('ownerId', '==', uid)),
    (snap) => {
      owned = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<CalendarNote, 'id'>) }));
      ownedReady = true;
      emit();
    },
    handleError
  );

  const unsubShared = onSnapshot(
    // 공유 메모만 — isShared 명시 true. (옛 데이터 호환은 lazy migration 으로 채움)
    query(
      collection(db, COLLECTION),
      where('appraisalId', '!=', null),
      where('isShared', '==', true)
    ),
    (snap) => {
      shared = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<CalendarNote, 'id'>) }));
      sharedReady = true;
      emit();
    },
    handleError
  );

  return () => {
    unsubOwned();
    unsubShared();
  };
}

/**
 * 본인 메모 중 사건 연결되어 있고 isShared 필드 누락된 것에 isShared=true 일괄 추가.
 * 사무소 공유 정책 도입(2026-05-25) 전 데이터를 호환시키기 위한 1회성 마이그레이션.
 * - 사용자가 캘린더 열 때마다 한 번 실행 (이미 마이그레이션 끝났으면 batch 비어있어 no-op)
 * - 옛 정책에서 사건 연결 = 자동 공유였으므로 isShared=true 가 자연스러운 default
 */
export async function migrateLegacySharedNotes(uid: string): Promise<number> {
  const snap = await getDocs(query(collection(db, COLLECTION), where('ownerId', '==', uid)));
  const batch = writeBatch(db);
  let count = 0;
  snap.forEach((d) => {
    const data = d.data();
    if (data.appraisalId && data.isShared === undefined) {
      batch.update(d.ref, { isShared: true });
      count++;
    }
  });
  if (count > 0) await batch.commit();
  return count;
}

/**
 * 특정 사건에 연결된 메모. 사건 상세 페이지 "관련 일정" 섹션 용.
 * - 본인 메모: 공유/비공유 모두 (사건 페이지에서 본인 메모는 다 봐야 함)
 * - 다른 사람 메모: isShared=true 만
 * 두 쿼리 결과 합쳐 dedup.
 */
export function subscribeNotesByAppraisalId(
  appraisalId: string,
  uid: string,
  onChange: (notes: CalendarNote[]) => void,
  onError?: (e: Error) => void
): () => void {
  let owned: CalendarNote[] = [];
  let shared: CalendarNote[] = [];
  let ownedReady = false;
  let sharedReady = false;

  const emit = () => {
    if (!ownedReady || !sharedReady) return;
    const map = new Map<string, CalendarNote>();
    for (const n of owned) map.set(n.id, n);
    for (const n of shared) if (!map.has(n.id)) map.set(n.id, n);
    onChange(Array.from(map.values()));
  };

  const handleError = (e: Error) => {
    if (onError) onError(e);
  };

  const unsubOwned = onSnapshot(
    query(
      collection(db, COLLECTION),
      where('appraisalId', '==', appraisalId),
      where('ownerId', '==', uid)
    ),
    (snap) => {
      owned = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<CalendarNote, 'id'>) }));
      ownedReady = true;
      emit();
    },
    handleError
  );

  const unsubShared = onSnapshot(
    query(
      collection(db, COLLECTION),
      where('appraisalId', '==', appraisalId),
      where('isShared', '==', true)
    ),
    (snap) => {
      shared = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<CalendarNote, 'id'>) }));
      sharedReady = true;
      emit();
    },
    handleError
  );

  return () => {
    unsubOwned();
    unsubShared();
  };
}

export function generateNoteId(): string {
  return `${Date.now()}-${Math.floor(Math.random() * 10000)}`;
}

export async function createNote(note: CalendarNoteInput): Promise<void> {
  await setDoc(doc(db, COLLECTION, note.id), {
    ...note,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
}

// 편집은 patch 를 받아 setDoc(merge:true) — 호출자가 deleteField() 로 명시적 제거 가능.
// (merge:true 는 payload 에 없는 필드를 기존값 그대로 두므로, 필드 제거에는 deleteField 가 필요.)
export async function updateNote(
  id: string,
  patch: Record<string, unknown>
): Promise<void> {
  await setDoc(
    doc(db, COLLECTION, id),
    { ...patch, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

export async function deleteNote(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, id));
}

/**
 * 메모 카테고리별 색상. theme.palette 에서 받아 호출.
 * 13-calendar-design.md §3.3 색상 룰.
 */
export function buildNoteCategoryColors(palette: {
  warning: { main: string; light?: string; dark?: string };
  secondary: { main: string; light?: string };
  info: { light?: string; main: string };
  grey: { 500: string; 700: string };
}): Record<NoteCategory, string> {
  return {
    meeting: palette.warning.main, // 🟡 회의
    visit: palette.warning.dark ?? palette.warning.main, // 🟧 방문 (orange-ish)
    work_block: palette.info.main, // 🟦 작업 (cyan/teal)
    followup: palette.secondary.main, // 🟣 follow-up
    personal: palette.grey[500], // ⚪ 개인
    other: palette.grey[700] // ⚫ 기타
  };
}
